/**
 * TruncatedText — collapses a long string to a short preview with a
 * "Show more" / "Show less" toggle. The expanded text renders in a
 * fixed-height, locally-scrollable code block so a large blob never dominates
 * the drawer.
 *
 * Used by the Result panel (`FormattedJsonView`) to keep base64 attachment /
 * signature blobs from dumping inline. Shared by ora + the Outlook add-in, so
 * it imports nothing app-specific.
 *
 * Import alias in ora:     ~/common/workflows/components/TruncatedText
 * Import alias in add-in:  @product-app/common/workflows/components/TruncatedText
 */
import { useId, useState } from "react";

export interface TruncatedTextProps {
  value: string;
  /** Characters shown in the collapsed preview. Default 100. */
  collapsedChars?: number;
}

const TOGGLE_CLASS =
  "ml-1 text-2xs font-semibold text-sunrise hover:underline focus:outline-none " +
  "focus-visible:ring-2 focus-visible:ring-sunrise rounded";

export function TruncatedText({ value, collapsedChars = 100 }: TruncatedTextProps) {
  const [expanded, setExpanded] = useState(false);
  const bodyId = useId();

  // Collapse runs of whitespace so a multi-line blob shows a clean one-line
  // preview rather than `collapsedChars` worth of newlines.
  const preview = value.replace(/\s+/g, " ").trim().slice(0, collapsedChars);
  const needsToggle = value.length > collapsedChars;

  return (
    <span className="inline-block max-w-full align-top" data-testid="truncated-text">
      {expanded ? (
        <pre
          id={bodyId}
          className="text-2xs font-mono bg-primary-gray-light-50 rounded-md p-2 max-h-[16rem] overflow-auto whitespace-pre-wrap break-words text-primary-gray-light-800 border border-primary-gray-light-200"
        >
          {value}
        </pre>
      ) : (
        <span className="text-primary-gray-light-900 break-words">
          {needsToggle ? `${preview}…` : value}
        </span>
      )}
      {needsToggle && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className={TOGGLE_CLASS}
          aria-expanded={expanded}
          aria-controls={expanded ? bodyId : undefined}
          data-testid="truncated-text-toggle"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </span>
  );
}

export default TruncatedText;
